import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useDialog from 'components/Dialog/useDialog';
import Popup from 'components/Popup';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import createOnboardingConfig from 'routes/Onboarding/config/onboardingConfig';

import InternalForm from 'components/InternalForm';
import InputSelect from 'components/Inputs/inputSelect';
import StartupSearcher from 'components/StartupSearcher';
import { Button } from 'xland-ui';
import { createInvestmentAsset, updateInvestmentAsset } from 'modules/investmentAssets/repository';

function PopupEditInvestment(props) {
  const {
    asset = null, organizations = '', onSubmit = () => {}, onClose,
  } = props;

  const user = useSelector(state => state.session.user);
  const literalsOnboarding = useSelector(state => state.i18n.literals.onboarding);
  const literalsInvestmentAssets = useSelector(state => state.i18n.literals.investmentAssets);
  const literalsCommon = useSelector(state => state.i18n.literals.common);
  const literals = { ...literalsOnboarding, ...literalsInvestmentAssets, ...literalsCommon };

  const [preOnboarding, setPreOnboarding] = useState(!asset?.id);
  const [investmentAsset, setInvestmentAsset] = useState(asset);
  const [type, setType] = useState(null);
  const [orgs, setOrgs] = useState(organizations ? organizations.split(',') : []);

  const [loading, setLoading] = useState(false);
  const { dialog } = useDialog();
  const navigate = useNavigate();

  const onboardingConfig = createOnboardingConfig({
    id: investmentAsset?.id,
    user,
    investmentAsset,
    organization: orgs.join(','),
    navigate,
    dialog,
    literals,
  });

  const handleInvestmentAssetSubmit = async (auxData) => {
    const data = {
      ...auxData,
      id: investmentAsset?.id,
      type: investmentAsset?.type || type,
      organization: orgs.join(','),
    };

    setLoading(true);
    try {
      if (data.id) {
        await updateInvestmentAsset(data.id, data);
      } else {
        await createInvestmentAsset(data);
      }
      onSubmit();
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popup
      title={literals.addInvestment}
      onClose={onClose}
    >
      {preOnboarding || (!type && !investmentAsset?.name) ? (
        <div>
          <div className='row'>
            <div className='col-12 col-md-6'>
              <InputSelect
                className='mb-0'
                preText={literals.type}
                placeholder={literals.type}
                value={type}
                onChange={setType}
                options={[
                  { id: 'company', name: literals.company },
                  { id: 'fund', name: literals.fund },
                  { id: 'listedCompany', name: literals.listedCompany },
                  { id: 'realEstate', name: literals.realEstate },
                  { id: 'crypto', name: literals.crypto },
                  { id: 'other', name: literals.other },
                ]}
              />
            </div>
            <div className='col-12 col-md-6'>
              <InputSelect
                className='mb-0'
                preText={literals.organizations}
                placeholder={literals.organizations}
                value={orgs}
                onChange={v => setOrgs(v)}
                options={user.organizations.map(o => ({ id: o.id, name: o.name }))}
                multiple
                isRequired
              />
            </div>
            <div className='col-12'>
              <StartupSearcher
                placeholder={type ? `${literals.writeName} ${literals[type]?.toLowerCase()}` : literals.selectType}
                value={investmentAsset || undefined}
                onChange={v => setInvestmentAsset(v?.id !== 'other' ? v : { ...investmentAsset, name: v.name })}
                type={type}
                isDisabled={!type}
                isRequired
              />
            </div>
          </div>
          <div className='buttons'>
            <Button
              text={literals.next}
              onClick={() => setPreOnboarding(false)}
              disabled={!type || !investmentAsset?.name}
            />
          </div>
        </div>
      ) : (
        <InternalForm
          type={investmentAsset?.type || type}
          answers={onboardingConfig.investment.getAnswers() || {}}
          buttons={[
            ...((!investmentAsset?.id) ? [{
              text: literals.back,
              onClick: () => setPreOnboarding(true),
            }] : []),
            {
              text: literals.save,
              type: 'submit',
              loading,
              onClick: async (v) => {
                await handleInvestmentAssetSubmit(v);
              },
            },
          ]}
          onLoad={() => {}}
        />
      )}
    </Popup>
  );
}

PopupEditInvestment.propTypes = {
  asset: PropTypes.object,
  organizations: PropTypes.array,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

PopupEditInvestment.defaultProps = {
  asset: null,
};

export default PopupEditInvestment;
